import { useState } from 'react';
import {
  IconBuildingsFilled,
  IconDollarSignCircleFilled,
  IconExternalOutline,
  IconHammerFilled,
  IconPlusFilled,
  IconSparkleFilled,
  IconTagOutline,
} from '@zillow/constellation-icons';
import { IconMapDot } from './IconMapDot';
import { SimpleCircleMapDot } from './SimpleCircleMapDot';
import { PriceMapDot } from './PriceMapDot';
import {
  ADVERTISED_PROPERTY_MARKET_MAP_DOT_COLOR,
  CLAIMED_PROPERTY_MARKET_MAP_DOT_COLOR,
  COMPETITOR_PROPERTY_CHECKED_COLOR,
  COMPETITOR_PROPERTY_UNCHECKED_COLOR,
  DARK_ICON_COLOR,
  PACKAGE_COLORS,
} from './Dots.consts';
import {
  AnalyticsProductLevel,
  type ClientAdProductType,
  type CompetitorWithOfferDto,
  type ConstructionDetailsDto,
  type ListingCompDto,
  MarketAnalyticsWebApiHandler_getConstructionDetails,
  type PortfolioAnalyticsPropertyResponseDto,
  type Property,
  type TopConstructionDto,
  TopConstructionStatus,
} from '@zg-rentals/ts-customer-analytics-client';
import { Box, Button, Paragraph, Spinner, Tag } from '@zillow/constellation-10';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import { formatNumber } from '../../../../utils';
import { format } from 'date-fns';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { isValidCompPrice } from '../../../../utils/isValidCompPrice';

export const PropertyMarketMapDot = ({
  property,
  bottomCircleColor,
  zIndex,
}: {
  property: PortfolioAnalyticsPropertyResponseDto;
  bottomCircleColor?: string;
  zIndex?: number;
}) => {
  const packageType = property.aggregateStats.adproductTypes[0];

  return (
    <IconMapDot
      lat={property.propertyDetails.lat}
      lng={property.propertyDetails.lon}
      icon={<IconBuildingsFilled />}
      backgroundColor={ADVERTISED_PROPERTY_MARKET_MAP_DOT_COLOR}
      bottomCircleColor={bottomCircleColor}
      zIndex={zIndex}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {property.propertyDetails.street}, {property.propertyDetails.city}
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
            {property.propertyDetails.city}, {property.propertyDetails.state} {property.propertyDetails.zip}
          </Paragraph>
          <Paragraph>
            <b>Package:</b> {mapPackageTypeName(packageType)}
          </Paragraph>
          <Paragraph>
            <b>Leads:</b> {formatNumber(property.aggregateStats.leadCount)}
          </Paragraph>
          <Paragraph>
            <b>Page views:</b> {formatNumber(property.aggregateStats.viewCount)}
          </Paragraph>
          <Paragraph>
            <b>Impressions:</b> {formatNumber(property.aggregateStats.impressionCount)}
          </Paragraph>
          <Paragraph>
            <b>Active units:</b> {formatNumber(property.propertyDetails.numUnits)}
          </Paragraph>
        </Box>
      }
      popoverTitle={property.propertyDetails.listingName}
    />
  );
};

export const ClaimedPropertyMarketMapDot = ({
  property,
  bottomCircleColor,
  zIndex,
}: {
  property: PortfolioAnalyticsPropertyResponseDto;
  bottomCircleColor?: string;
  zIndex?: number;
}) => {
  return (
    <IconMapDot
      lat={property.propertyDetails.lat}
      lng={property.propertyDetails.lon}
      icon={<IconBuildingsFilled />}
      backgroundColor={CLAIMED_PROPERTY_MARKET_MAP_DOT_COLOR}
      iconColor={DARK_ICON_COLOR}
      bottomCircleColor={bottomCircleColor}
      zIndex={zIndex}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {property.propertyDetails.street}, {property.propertyDetails.city}
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
            {property.propertyDetails.city}, {property.propertyDetails.state} {property.propertyDetails.zip}
          </Paragraph>
          <Paragraph>
            <b>Leads:</b> {formatNumber(property.aggregateStats.leadCount)}
          </Paragraph>
          <Paragraph>
            <b>Page views:</b> {formatNumber(property.aggregateStats.viewCount)}
          </Paragraph>
          <Paragraph>
            <b>Impressions:</b> {formatNumber(property.aggregateStats.impressionCount)}
          </Paragraph>
          <Paragraph>
            <b>Active units:</b> {formatNumber(property.propertyDetails.numUnits)}
          </Paragraph>
        </Box>
      }
      popoverTitle={property.propertyDetails.listingName}
      popoverHeaderAction={
        <Tag tone="gray" size="sm">
          Claimed
        </Tag>
      }
    />
  );
};

export type CompetitorMapDisplayMode =
  | 'default'
  | 'specialOffer'
  | 'lowPrice'
  | 'highPrice'
  | 'viewsRank'
  | 'leadsRank';

export const CompetitorPropertyMapDot = ({
  comp,
  isChecked,
  isSelectedProperty = false,
  isOffMarket = false,
  bottomCircleColor,
  zIndex,
  displayMode = 'default',
  viewsRank,
  leadsRank,
}: {
  comp: ListingCompDto;
  isChecked: boolean;
  isSelectedProperty?: boolean;
  isOffMarket?: boolean;
  bottomCircleColor?: string;
  zIndex?: number;
  displayMode?: CompetitorMapDisplayMode;
  viewsRank?: number;
  leadsRank?: number;
}) => {
  const housingTags: Array<{ label: string; tone: 'purple' | 'orange' | 'yellow' | 'teal' | 'aqua' | 'gray' }> = [];

  if (comp.isDisabledHousing) {
    housingTags.push({ label: 'Disabled housing', tone: 'purple' });
  }
  if (comp.isMilitaryHousing) {
    housingTags.push({ label: 'Military housing', tone: 'orange' });
  }
  if (comp.isIncomeRestricted) {
    housingTags.push({ label: 'Income restricted', tone: 'yellow' });
  }
  if (comp.isSeniorHousing) {
    housingTags.push({ label: 'Senior housing', tone: 'teal' });
  }
  if (comp.isStudentHousing) {
    housingTags.push({ label: 'Student housing', tone: 'aqua' });
  }
  if (comp.isUnclaimed) {
    housingTags.push({ label: 'Unclaimed', tone: 'gray' });
  }

  const advertisingBase = comp.hasOtherProductType ? 'Other' : mapPackageTypeName(comp.currentProductType);
  const advertisingFeatures = comp.productFeatures?.filter(
    (f) => f !== AnalyticsProductLevel.NONE && f !== AnalyticsProductLevel.ALL,
  );
  const advertisingLabel =
    advertisingFeatures && advertisingFeatures.length > 0
      ? `${advertisingBase} (${advertisingFeatures.join(', ')})`
      : advertisingBase;

  const popoverContent = (
    <Box>
      <Paragraph textStyle="body-bold">{comp.street}</Paragraph>
      <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
        {comp.city}, {comp.state} {comp.zip}
      </Paragraph>
      {(housingTags.length > 0 || isOffMarket) && (
        <Box css={{ display: 'flex', flexWrap: 'wrap', gap: '200', marginBlockEnd: '200' }}>
          {isOffMarket && (
            <Tag tone="orange" size="sm">
              Off market
            </Tag>
          )}
          {housingTags.map((tag) => (
            <Tag key={tag.label} tone={tag.tone} size="sm">
              {tag.label}
            </Tag>
          ))}
        </Box>
      )}
      <Paragraph>
        <b>Distance:</b> {comp.distanceMiles.toFixed(2)} mi
      </Paragraph>
      {(comp.overallLowPrice || comp.overallHighPrice) && isValidCompPrice(comp) && (
        <Paragraph>
          <b>Price range:</b>{' '}
          {comp.overallLowPrice && comp.overallHighPrice
            ? `${formatCurrency(comp.overallLowPrice)} - ${formatCurrency(comp.overallHighPrice)}`
            : comp.overallLowPrice
              ? formatCurrency(comp.overallLowPrice)
              : formatCurrency(comp.overallHighPrice!)}
        </Paragraph>
      )}
      {comp.numUnits && (
        <Paragraph>
          <b>Units:</b> {formatNumber(comp.numUnits)}
        </Paragraph>
      )}
      {comp.yearBuilt && (
        <Paragraph>
          <b>Year built:</b> {comp.yearBuilt}
        </Paragraph>
      )}
      {(comp.currentProductType || comp.hasOtherProductType) && (
        <Paragraph>
          <b>Advertising:</b> {advertisingLabel}
        </Paragraph>
      )}
      {comp.specialOfferDescription && (
        <Paragraph css={{ marginBlockStart: '200' }}>
          <b>Special offer:</b> {comp.specialOfferDescription}
        </Paragraph>
      )}
      {!isSelectedProperty && (
        <Box css={{ marginBlockStart: '400' }}>
          <Button
            size="sm"
            emphasis={isChecked ? 'outlined' : 'filled'}
            data-comp-id={comp.listingAlias}
            data-action="toggle-comp"
            css={{ width: '100%' }}
          >
            {isChecked ? 'Deselect this competitor' : 'Select this competitor'}
          </Button>
        </Box>
      )}
    </Box>
  );

  const getTagLabel = () => {
    if (isSelectedProperty) return 'Selected property';
    if (comp.isUserOwned) return 'In your portfolio';
    return 'Competitor';
  };

  const getTagTone = () => {
    if (isSelectedProperty) return 'blue';
    if (comp.isUserOwned) return 'blue';
    if (isChecked) return 'blue';
    return 'gray';
  };

  const popoverHeaderAction = (
    <Box css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Tag tone={getTagTone()} size="sm">
        {getTagLabel()}
      </Tag>
      {comp.zillowLink && (
        <a
          href={comp.zillowLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', color: '#0066cc', textDecoration: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconExternalOutline style={{ width: '16px', height: '16px' }} />
        </a>
      )}
    </Box>
  );

  // Special offer mode
  if (displayMode === 'specialOffer') {
    // Selected property always shows in special offer mode even without an offer
    if (isSelectedProperty) {
      if (comp.specialOfferDescription) {
        return (
          <IconMapDot
            lat={comp.latitude}
            lng={comp.longitude}
            icon={<IconTagOutline />}
            backgroundColor="#0041D9"
            iconColor="#FFFFFF"
            bottomCircleColor={bottomCircleColor}
            bottomCircleStyle="plain"
            zIndex={zIndex}
            popoverContent={popoverContent}
            popoverTitle={comp.propertyName}
            popoverHeaderAction={popoverHeaderAction}
          />
        );
      }
      // Show default blue icon if no special offer
      return (
        <IconMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          icon={<IconBuildingsFilled />}
          backgroundColor="#0041D9"
          iconColor="#FFFFFF"
          bottomCircleColor={bottomCircleColor}
          bottomCircleStyle="plain"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }

    if (!comp.specialOfferDescription) {
      return null;
    }

    return (
      <IconMapDot
        lat={comp.latitude}
        lng={comp.longitude}
        icon={<IconTagOutline />}
        backgroundColor="#22C55E"
        iconColor="#FFFFFF"
        bottomCircleColor={bottomCircleColor}
        bottomCircleStyle="plain"
        zIndex={zIndex}
        popoverContent={popoverContent}
        popoverTitle={comp.propertyName}
        popoverHeaderAction={popoverHeaderAction}
      />
    );
  }

  // Low price mode
  if (displayMode === 'lowPrice') {
    const hasValidLowPrice = comp.overallLowPrice && isValidCompPrice(comp);
    // Show selected property with price if available, otherwise show icon
    if (isSelectedProperty) {
      if (hasValidLowPrice) {
        return (
          <PriceMapDot
            lat={comp.latitude}
            lng={comp.longitude}
            price={comp.overallLowPrice}
            backgroundColor="#0041D9"
            textColor="#FFFFFF"
            zIndex={zIndex}
            popoverContent={popoverContent}
            popoverTitle={comp.propertyName}
            popoverHeaderAction={popoverHeaderAction}
          />
        );
      }
      // No price data - show default icon
      return (
        <IconMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          icon={<IconBuildingsFilled />}
          backgroundColor="#0041D9"
          iconColor="#FFFFFF"
          bottomCircleColor={bottomCircleColor}
          bottomCircleStyle="plain"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }

    // Competitor properties only show if they have price data
    if (hasValidLowPrice) {
      const backgroundColor = isChecked ? COMPETITOR_PROPERTY_CHECKED_COLOR : COMPETITOR_PROPERTY_UNCHECKED_COLOR;
      return (
        <PriceMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          price={comp.overallLowPrice}
          backgroundColor={backgroundColor}
          textColor="#FFFFFF"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }
  }

  // High price mode
  if (displayMode === 'highPrice') {
    const hasValidHighPrice = comp.overallHighPrice && isValidCompPrice(comp);
    // Show selected property with price if available, otherwise show icon
    if (isSelectedProperty) {
      if (hasValidHighPrice) {
        return (
          <PriceMapDot
            lat={comp.latitude}
            lng={comp.longitude}
            price={comp.overallHighPrice}
            backgroundColor="#0041D9"
            textColor="#FFFFFF"
            zIndex={zIndex}
            popoverContent={popoverContent}
            popoverTitle={comp.propertyName}
            popoverHeaderAction={popoverHeaderAction}
          />
        );
      }
      // No price data - show default icon
      return (
        <IconMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          icon={<IconBuildingsFilled />}
          backgroundColor="#0041D9"
          iconColor="#FFFFFF"
          bottomCircleColor={bottomCircleColor}
          bottomCircleStyle="plain"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }

    // Competitor properties only show if they have price data
    if (hasValidHighPrice) {
      const backgroundColor = isChecked ? COMPETITOR_PROPERTY_CHECKED_COLOR : COMPETITOR_PROPERTY_UNCHECKED_COLOR;
      return (
        <PriceMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          price={comp.overallHighPrice}
          backgroundColor={backgroundColor}
          textColor="#FFFFFF"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }
  }

  // Views rank mode
  if (displayMode === 'viewsRank') {
    // Show selected property with rank if available, otherwise show icon
    if (isSelectedProperty) {
      if (viewsRank) {
        return (
          <PriceMapDot
            lat={comp.latitude}
            lng={comp.longitude}
            price={viewsRank}
            backgroundColor="#0041D9"
            textColor="#FFFFFF"
            zIndex={zIndex}
            popoverContent={popoverContent}
            popoverTitle={comp.propertyName}
            popoverHeaderAction={popoverHeaderAction}
            showDollarSign={false}
          />
        );
      }
      // No rank data - show default icon
      return (
        <IconMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          icon={<IconBuildingsFilled />}
          backgroundColor="#0041D9"
          iconColor="#FFFFFF"
          bottomCircleColor={bottomCircleColor}
          bottomCircleStyle="plain"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }

    // Competitor properties only show if they have rank data
    if (viewsRank) {
      const backgroundColor = isChecked ? COMPETITOR_PROPERTY_CHECKED_COLOR : COMPETITOR_PROPERTY_UNCHECKED_COLOR;
      return (
        <PriceMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          price={viewsRank}
          backgroundColor={backgroundColor}
          textColor="#FFFFFF"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
          showDollarSign={false}
        />
      );
    }
  }

  // Leads rank mode
  if (displayMode === 'leadsRank') {
    // Show selected property with rank if available, otherwise show icon
    if (isSelectedProperty) {
      if (leadsRank) {
        return (
          <PriceMapDot
            lat={comp.latitude}
            lng={comp.longitude}
            price={leadsRank}
            backgroundColor="#0041D9"
            textColor="#FFFFFF"
            zIndex={zIndex}
            popoverContent={popoverContent}
            popoverTitle={comp.propertyName}
            popoverHeaderAction={popoverHeaderAction}
            showDollarSign={false}
          />
        );
      }
      // No rank data - show default icon
      return (
        <IconMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          icon={<IconBuildingsFilled />}
          backgroundColor="#0041D9"
          iconColor="#FFFFFF"
          bottomCircleColor={bottomCircleColor}
          bottomCircleStyle="plain"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
        />
      );
    }

    // Competitor properties only show if they have rank data
    if (leadsRank) {
      const backgroundColor = isChecked ? COMPETITOR_PROPERTY_CHECKED_COLOR : COMPETITOR_PROPERTY_UNCHECKED_COLOR;
      return (
        <PriceMapDot
          lat={comp.latitude}
          lng={comp.longitude}
          price={leadsRank}
          backgroundColor={backgroundColor}
          textColor="#FFFFFF"
          zIndex={zIndex}
          popoverContent={popoverContent}
          popoverTitle={comp.propertyName}
          popoverHeaderAction={popoverHeaderAction}
          showDollarSign={false}
        />
      );
    }
  }

  // Default mode
  if (isSelectedProperty) {
    return (
      <IconMapDot
        lat={comp.latitude}
        lng={comp.longitude}
        icon={<IconBuildingsFilled />}
        backgroundColor="#0041D9"
        iconColor="#FFFFFF"
        bottomCircleColor={bottomCircleColor}
        bottomCircleStyle="plain"
        zIndex={zIndex}
        popoverContent={popoverContent}
        popoverTitle={comp.propertyName}
        popoverHeaderAction={popoverHeaderAction}
      />
    );
  }

  if (!isChecked) {
    return (
      <SimpleCircleMapDot
        lat={comp.latitude}
        lng={comp.longitude}
        color={bottomCircleColor ?? COMPETITOR_PROPERTY_UNCHECKED_COLOR}
        size={12}
        zIndex={zIndex}
        popoverContent={popoverContent}
        popoverTitle={comp.propertyName}
        popoverHeaderAction={popoverHeaderAction}
      />
    );
  }

  return (
    <IconMapDot
      lat={comp.latitude}
      lng={comp.longitude}
      icon={<IconBuildingsFilled />}
      backgroundColor={COMPETITOR_PROPERTY_CHECKED_COLOR}
      iconColor={DARK_ICON_COLOR}
      bottomCircleColor={bottomCircleColor}
      bottomCircleStyle="plain"
      zIndex={zIndex}
      popoverContent={popoverContent}
      popoverTitle={comp.propertyName}
      popoverHeaderAction={popoverHeaderAction}
    />
  );
};

export const TopLeadVolumePropertyMarketMapDot = ({
  property,
  bottomCircleColor,
}: {
  property: PortfolioAnalyticsPropertyResponseDto;
  bottomCircleColor?: string;
}) => {
  const packageType = property.aggregateStats.adproductTypes[0];

  return (
    <IconMapDot
      lat={property.propertyDetails.lat}
      lng={property.propertyDetails.lon}
      icon={<IconBuildingsFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {property.propertyDetails.street}, {property.propertyDetails.city}
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
            {property.propertyDetails.city}, {property.propertyDetails.state} {property.propertyDetails.zip}
          </Paragraph>
          <Paragraph>
            <b>Package:</b> {mapPackageTypeName(packageType)}
          </Paragraph>
          <Paragraph>
            <b>Leads:</b> {formatNumber(property.aggregateStats.leadCount)}
          </Paragraph>
          <Paragraph>
            <b>Page views:</b> {formatNumber(property.aggregateStats.viewCount)}
          </Paragraph>
          <Paragraph>
            <b>Impressions:</b> {formatNumber(property.aggregateStats.impressionCount)}
          </Paragraph>
          <Paragraph>
            <b>Active units:</b> {formatNumber(property.propertyDetails.numUnits)}
          </Paragraph>
        </Box>
      }
    />
  );
};

export const TopLeadOpportunitiesPropertyMarketMapDot = ({
  property,
  bottomCircleColor,
}: {
  property: PortfolioAnalyticsPropertyResponseDto;
  bottomCircleColor?: string;
}) => {
  const packageType = property.aggregateStats.adproductTypes[0];

  return (
    <IconMapDot
      lat={property.propertyDetails.lat}
      lng={property.propertyDetails.lon}
      icon={<IconBuildingsFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {property.propertyDetails.street}, {property.propertyDetails.city}
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
            {property.propertyDetails.city}, {property.propertyDetails.state} {property.propertyDetails.zip}
          </Paragraph>
          <Paragraph>
            <b>Package:</b> {mapPackageTypeName(packageType)}
          </Paragraph>
          <Paragraph>
            <b>Leads:</b> {formatNumber(property.aggregateStats.leadCount)}
          </Paragraph>
          <Paragraph>
            <b>Page views:</b> {formatNumber(property.aggregateStats.viewCount)}
          </Paragraph>
          <Paragraph>
            <b>Impressions:</b> {formatNumber(property.aggregateStats.impressionCount)}
          </Paragraph>
          <Paragraph>
            <b>Active units:</b> {formatNumber(property.propertyDetails.numUnits)}
          </Paragraph>
        </Box>
      }
    />
  );
};

export const SpecialOffersMarketMapDot = ({
  competitor,
  bottomCircleColor,
}: {
  competitor: CompetitorWithOfferDto;
  bottomCircleColor?: string;
}) => {
  const [address1, ...address2] = competitor.listingAddress.split(',').map((part) => part.trim());

  return (
    <IconMapDot
      lat={competitor.lat}
      lng={competitor.lon}
      icon={<IconDollarSignCircleFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">{address1}</Paragraph>
          <Paragraph textStyle="body-bold">{address2.join(', ')}</Paragraph>
          {competitor.yearBuilt && (
            <Paragraph>
              <b>Year built:</b> {competitor.yearBuilt}
            </Paragraph>
          )}
          <Paragraph css={{ marginBlockEnd: '200' }}>
            <b>Units:</b> {competitor.numUnits}
          </Paragraph>
          <Paragraph>
            <b>Special offer:</b> {competitor.offerDescription}
          </Paragraph>
        </Box>
      }
      popoverTitle={competitor.name}
      popoverHeaderAction={
        <Tag tone="green" size="sm">
          Special offers
        </Tag>
      }
    />
  );
};

export const NewInventoryOnZillowMarketMapDot = ({
  newInventory,
  bottomCircleColor,
}: {
  newInventory: Property;
  bottomCircleColor?: string;
}) => {
  return (
    <IconMapDot
      lat={newInventory.data.propertyInfo.geoRegion.latitude}
      lng={newInventory.data.propertyInfo.geoRegion.longitude}
      icon={<IconPlusFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {newInventory.data.propertyInfo.address.houseNumber} {newInventory.data.propertyInfo.address.streetName}{' '}
            {newInventory.data.propertyInfo.address.streetSuffix}
          </Paragraph>
          <Paragraph textStyle="body-bold">
            {newInventory.data.propertyInfo.address.city}, {newInventory.data.propertyInfo.address.state}{' '}
            {newInventory.data.propertyInfo.address.zipPlusFour}
          </Paragraph>
        </Box>
      }
      popoverTitle={newInventory.data.propertyInfo.propertyName || 'New Inventory on Zillow'}
      popoverHeaderAction={
        <Tag tone="green" size="sm">
          New Inventory
        </Tag>
      }
    />
  );
};

export const NowLeasingMarketMapDot = ({
  topNewConstruction,
  bottomCircleColor,
}: {
  topNewConstruction: TopConstructionDto;
  bottomCircleColor?: string;
}) => {
  const [address1, ...address2] = topNewConstruction.fullAddress.split(',').map((part) => part.trim());
  const [isLoading, setIsLoading] = useState(false);
  const [constructionDetails, setConstructionDetails] = useState<ConstructionDetailsDto | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const handleOpen = async () => {
    if (hasFetched) return;

    setIsLoading(true);
    try {
      const details = await MarketAnalyticsWebApiHandler_getConstructionDetails({
        address: topNewConstruction.fullAddress,
      });

      setConstructionDetails(details);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch construction details:', error);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  };

  const renderPopoverContent = () => {
    if (isLoading) {
      return (
        <Box css={{ display: 'flex', justifyContent: 'center', padding: '400' }}>
          <Spinner size="md" />
        </Box>
      );
    }

    const numberOfUnits = constructionDetails?.numberOfUnits ?? topNewConstruction.numberOfUnits;
    const overallLowPrice = constructionDetails?.overallLowPrice ?? topNewConstruction.overallLowPrice;
    const overallHighPrice = constructionDetails?.overallHighPrice ?? topNewConstruction.overallHighPrice;
    const specialOffer = constructionDetails?.specialOffer ?? topNewConstruction.specialOffer;

    return (
      <Box>
        <Paragraph>{address1}</Paragraph>
        <Paragraph css={{ marginBlockEnd: '200' }}>{address2.join(', ')}</Paragraph>
        <Paragraph>
          <strong>Status:</strong> Now leasing
        </Paragraph>
        {overallHighPrice && overallLowPrice && (
          <Paragraph>
            <strong>Rent range:</strong> {formatCurrency(overallLowPrice)} - {formatCurrency(overallHighPrice)}
          </Paragraph>
        )}
        <Paragraph css={{ marginBlockEnd: '200' }}>
          <strong>Number of units:</strong> {numberOfUnits}
        </Paragraph>
        {specialOffer && (
          <Paragraph>
            <strong>Special offer:</strong> {specialOffer}
          </Paragraph>
        )}
      </Box>
    );
  };

  return (
    <IconMapDot
      lat={topNewConstruction.lat}
      lng={topNewConstruction.lon}
      icon={<IconSparkleFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={renderPopoverContent()}
      popoverTitle={topNewConstruction.buildingName}
      popoverHeaderAction={
        <Tag tone="aqua" size="sm">
          Now leasing
        </Tag>
      }
      onOpen={handleOpen}
    />
  );
};

export const UnderConstructionMarketMapDot = ({
  topNewConstruction,
  bottomCircleColor,
}: {
  topNewConstruction: TopConstructionDto;
  bottomCircleColor?: string;
}) => {
  const [address1, ...address2] = topNewConstruction.fullAddress.split(',').map((part) => part.trim());
  return (
    <IconMapDot
      lat={topNewConstruction.lat}
      lng={topNewConstruction.lon}
      icon={<IconHammerFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph>{address1}</Paragraph>
          <Paragraph css={{ marginBlockEnd: '200' }}>{address2.join(', ')}</Paragraph>
          <Paragraph>
            <b>Status:</b> Under construction
          </Paragraph>
          {topNewConstruction.startedDate && (
            <Paragraph>
              <b>Date started:</b> {format(localDateToDate(topNewConstruction.startedDate), 'MMMM yyyy')}
            </Paragraph>
          )}
          {topNewConstruction.estimatedCompletionDate && (
            <Paragraph>
              <b>Estimated completion:</b>{' '}
              {format(localDateToDate(topNewConstruction.estimatedCompletionDate), 'MMMM yyyy')}
            </Paragraph>
          )}
          <Paragraph>
            <b>Number of units:</b> {topNewConstruction.numberOfUnits}
          </Paragraph>
        </Box>
      }
      popoverTitle={topNewConstruction.buildingName}
      popoverHeaderAction={
        <Tag tone="gray" size="sm">
          In progress
        </Tag>
      }
    />
  );
};

export const PlannedDevelopmentMarketMapDot = ({
  topNewConstruction,
  bottomCircleColor,
}: {
  topNewConstruction: TopConstructionDto;
  bottomCircleColor?: string;
}) => {
  const [address1, ...address2] = topNewConstruction.fullAddress.split(',').map((part) => part.trim());
  return (
    <IconMapDot
      lat={topNewConstruction.lat}
      lng={topNewConstruction.lon}
      icon={<IconHammerFilled />}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph>{address1}</Paragraph>
          <Paragraph css={{ marginBlockEnd: '200' }}>{address2.join(', ')}</Paragraph>
          <Paragraph>
            <b>Status:</b> Planned development
          </Paragraph>
          {topNewConstruction.startedDate && (
            <Paragraph>
              <b>Date started:</b> {format(localDateToDate(topNewConstruction.startedDate), 'MMMM yyyy')}
            </Paragraph>
          )}
          {topNewConstruction.estimatedCompletionDate && (
            <Paragraph>
              <b>Estimated completion:</b>{' '}
              {format(localDateToDate(topNewConstruction.estimatedCompletionDate), 'MMMM yyyy')}
            </Paragraph>
          )}
          <Paragraph>
            <b>Number of units:</b> {topNewConstruction.numberOfUnits}
          </Paragraph>
        </Box>
      }
      popoverTitle={topNewConstruction.buildingName}
      popoverHeaderAction={
        <Tag tone="gray" size="sm">
          Planned development
        </Tag>
      }
    />
  );
};

export const NewConstructionDot = ({
  topNewConstruction,
  bottomCircleColor,
}: {
  topNewConstruction: TopConstructionDto;
  bottomCircleColor?: string;
}) => {
  if (topNewConstruction.constructionStatus === TopConstructionStatus.COMPLETE) {
    return <NowLeasingMarketMapDot topNewConstruction={topNewConstruction} bottomCircleColor={bottomCircleColor} />;
  }
  if (topNewConstruction.constructionStatus === TopConstructionStatus.PLANNED_DEVELOPMENT) {
    return (
      <PlannedDevelopmentMarketMapDot topNewConstruction={topNewConstruction} bottomCircleColor={bottomCircleColor} />
    );
  }

  return (
    <UnderConstructionMarketMapDot topNewConstruction={topNewConstruction} bottomCircleColor={bottomCircleColor} />
  );
};

export const PackageMarketMapDot = ({
  property,
  bottomCircleColor,
}: {
  property: PortfolioAnalyticsPropertyResponseDto & { packageType: ClientAdProductType };
  bottomCircleColor?: string;
}) => {
  const packageType = property.aggregateStats.adproductTypes[0];

  return (
    <IconMapDot
      lat={property.propertyDetails.lat}
      lng={property.propertyDetails.lon}
      icon={<IconBuildingsFilled />}
      backgroundColor={PACKAGE_COLORS[property.packageType]}
      bottomCircleColor={bottomCircleColor}
      popoverContent={
        <Box>
          <Paragraph textStyle="body-bold">
            {property.propertyDetails.street}, {property.propertyDetails.city}
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBlockEnd: '200' }}>
            {property.propertyDetails.city}, {property.propertyDetails.state} {property.propertyDetails.zip}
          </Paragraph>
          <Paragraph>
            <b>Package:</b> {mapPackageTypeName(packageType)}
          </Paragraph>
          <Paragraph>
            <b>Leads:</b> {formatNumber(property.aggregateStats.leadCount)}
          </Paragraph>
          <Paragraph>
            <b>Page views:</b> {formatNumber(property.aggregateStats.viewCount)}
          </Paragraph>
          <Paragraph>
            <b>Impressions:</b> {formatNumber(property.aggregateStats.impressionCount)}
          </Paragraph>
          <Paragraph>
            <b>Active units:</b> {formatNumber(property.propertyDetails.numUnits)}
          </Paragraph>
        </Box>
      }
      popoverTitle={property.propertyDetails.listingName}
    />
  );
};

export type MapDotPayload =
  | {
      type: 'property';
      data: PortfolioAnalyticsPropertyResponseDto;
      visible?: boolean;
      bottomCircleColor?: string;
      zIndex?: number;
    }
  | {
      type: 'claimedProperty';
      data: PortfolioAnalyticsPropertyResponseDto;
      visible?: boolean;
      bottomCircleColor?: string;
      zIndex?: number;
    }
  | {
      type: 'competitorProperty';
      data: ListingCompDto;
      isChecked: boolean;
      isSelectedProperty?: boolean;
      isOffMarket?: boolean;
      visible?: boolean;
      bottomCircleColor?: string;
      zIndex?: number;
      displayMode?: CompetitorMapDisplayMode;
      viewsRank?: number;
      leadsRank?: number;
    }
  | {
      type: 'topLeadVolume';
      data: PortfolioAnalyticsPropertyResponseDto;
      visible?: boolean;
      bottomCircleColor?: string;
    }
  | {
      type: 'topLeadOpportunities';
      data: PortfolioAnalyticsPropertyResponseDto;
      visible?: boolean;
      bottomCircleColor?: string;
    }
  | { type: 'specialOffers'; data: CompetitorWithOfferDto; visible?: boolean; bottomCircleColor?: string }
  | { type: 'newInventory'; data: Property; visible?: boolean; bottomCircleColor?: string }
  | { type: 'newConstruction'; data: TopConstructionDto; visible?: boolean; bottomCircleColor?: string }
  | {
      type: 'package';
      data: PortfolioAnalyticsPropertyResponseDto & { packageType: ClientAdProductType };
      visible?: boolean;
      bottomCircleColor?: string;
    };

export const MapDot = ({ payload }: { payload: MapDotPayload }) => {
  if (payload.visible === false) {
    return null;
  }

  switch (payload.type) {
    case 'property':
      return (
        <PropertyMarketMapDot
          property={payload.data}
          bottomCircleColor={payload.bottomCircleColor}
          zIndex={payload.zIndex}
        />
      );
    case 'claimedProperty':
      return (
        <ClaimedPropertyMarketMapDot
          property={payload.data}
          bottomCircleColor={payload.bottomCircleColor}
          zIndex={payload.zIndex}
        />
      );
    case 'competitorProperty':
      return (
        <CompetitorPropertyMapDot
          comp={payload.data}
          isChecked={payload.isChecked}
          isSelectedProperty={payload.isSelectedProperty}
          isOffMarket={payload.isOffMarket}
          bottomCircleColor={payload.bottomCircleColor}
          zIndex={payload.zIndex}
          displayMode={payload.displayMode}
          viewsRank={payload.viewsRank}
          leadsRank={payload.leadsRank}
        />
      );
    case 'topLeadVolume':
      return (
        <TopLeadVolumePropertyMarketMapDot property={payload.data} bottomCircleColor={payload.bottomCircleColor} />
      );
    case 'topLeadOpportunities':
      return (
        <TopLeadOpportunitiesPropertyMarketMapDot
          property={payload.data}
          bottomCircleColor={payload.bottomCircleColor}
        />
      );
    case 'specialOffers':
      return <SpecialOffersMarketMapDot competitor={payload.data} bottomCircleColor={payload.bottomCircleColor} />;
    case 'newInventory':
      return (
        <NewInventoryOnZillowMarketMapDot newInventory={payload.data} bottomCircleColor={payload.bottomCircleColor} />
      );
    case 'newConstruction':
      return <NewConstructionDot topNewConstruction={payload.data} bottomCircleColor={payload.bottomCircleColor} />;
    case 'package':
      return <PackageMarketMapDot property={payload.data} bottomCircleColor={payload.bottomCircleColor} />;
  }
};
